import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, MapPin, Star, BookOpen, Users, Trophy, Phone, Globe, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactWidget from '../components/ContactWidget';
import SEO from '../components/SEO';
import FAQ, { FAQ_DATA } from '../components/FAQ';
import { getLocalBusinessSchema, getFaqSchema } from '../utils/geoSchema';

const Home = () => {
  const localSchema = getLocalBusinessSchema();
  const faqSchema = getFaqSchema(FAQ_DATA);

  const subjects = [
    { title: "Class I to V Tuitions", icon: BookOpen, color: "bg-blue-100 text-blue-600" },
    { title: "Class VI to VIII Tuitions", icon: BookOpen, color: "bg-green-100 text-green-600" },
    { title: "Class IX to X Tuitions", icon: BookOpen, color: "bg-purple-100 text-purple-600" },
    { title: "Class XI to XII Tuitions", icon: BookOpen, color: "bg-pink-100 text-pink-600" },
    { title: "EAMCET Preparation", icon: Trophy, color: "bg-yellow-100 text-yellow-600" },
    { title: "IITJEE ADV & MAINS", icon: Star, color: "bg-red-100 text-red-600" },
    { title: "GRE / GMAT / SAT", icon: Globe, color: "bg-indigo-100 text-indigo-600" },
    { title: "Medicine & Engg Subjects", icon: Monitor, color: "bg-teal-100 text-teal-600" }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col pt-24 font-sans">
      <SEO 
        title="Top Home Tuitions in Hyderabad & Online Classes"
        description="Excellent 100% result-oriented home and online tuitions with guaranteed success rate and affordable prices across Hyderabad. Book verified tutors today."
        keywords="top home tuitions in hyderabad, home tuitions hyderabad, private tutor hyderabad, best home tuitions in hyderabad, online tuitions, math home tutor, science tutor, klm home tuitions"
        schema={[localSchema, faqSchema]}
      />

      {/* Bento Box Grid - Subjects (Moved to Top) */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 mb-12">
          
          {/* Promo Box (Left) */}
          <Link to="/contact#tech-solutions" className="w-full lg:w-1/3 block relative group overflow-hidden rounded-3xl shadow-xl border-2 border-brand-gold hover:shadow-2xl hover:-translate-y-1 transition-all">
            <div className="absolute inset-0 bg-slate-900" />
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=600&q=80')] opacity-20 bg-cover bg-center mix-blend-overlay group-hover:scale-110 transition-transform duration-700" />
            <div className="relative z-10 flex flex-col items-center justify-center text-center p-6 h-full border border-white/10 rounded-3xl">
              <Monitor className="w-8 h-8 text-brand-gold mb-2" />
              <h3 className="text-xl font-extrabold text-white leading-tight mb-3 tracking-wide">Get Your Own Website</h3>
              <div className="bg-gradient-to-r from-primary-500 to-brand-gold text-white px-5 py-2 rounded-full font-black text-lg shadow-xl mb-3 transform group-hover:scale-105 transition-transform">
                200/- month + Domain
              </div>
              <div className="flex items-center text-brand-gold font-bold text-xs uppercase tracking-widest group-hover:text-white transition-colors">
                Contact Tech Solutions <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          </Link>

          {/* Heading (Right) */}
          <div className="w-full lg:w-2/3 text-center lg:text-right">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4 leading-tight">Top Website Designer <br className="hidden lg:block"/>& Top Home Tuitions in Hyderabad</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-xl mx-auto lg:ml-auto lg:mr-0">We offer specialized tutoring across all major educational boards and competitive exams.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[200px]">
          {/* Large Featured Box */}
          <Link to="/register" className="col-span-1 md:col-span-2 row-span-2 group relative overflow-hidden rounded-3xl bg-slate-900 p-8 flex flex-col justify-end transition-transform hover:-translate-y-1">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600/40 to-brand-gold/40 opacity-50 group-hover:opacity-70 transition-opacity" />
            <img src="/images/web_design_hero.jpg" alt="Students learning" className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-50 group-hover:scale-105 transition-transform duration-700" />
            <div className="relative z-10 space-y-4">
              <div className="inline-block px-4 py-1.5 bg-brand-gold text-white font-bold rounded-full text-sm shadow-lg">Premium</div>
              <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight">IITJEE & NEET <br/> Preparation</h3>
              <p className="text-slate-200">Guaranteed results with top-tier faculty.</p>
            </div>
          </Link>

          {/* Regular Boxes */}
          {subjects.map((sub, i) => (
            <Link key={i} to="/register" className="group rounded-3xl bg-white dark:bg-slate-800 p-6 shadow-lg hover:shadow-xl border border-slate-100 dark:border-slate-700 transition-all hover:-translate-y-1 flex flex-col justify-between">
              <div className={`w-12 h-12 rounded-2xl ${sub.color} flex items-center justify-center`}>
                <sub.icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-primary-600 transition-colors">{sub.title}</h3>
                <div className="mt-2 text-sm font-semibold text-slate-500 flex items-center gap-1 group-hover:text-primary-600">
                  Learn more <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Hero Section (Moved Below) */}
      <section className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center border-t border-slate-200 dark:border-slate-800">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto space-y-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold text-sm mb-4">
            <Star className="w-4 h-4" /> Trusted by 10,000+ Students
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            Unlock Your Potential with <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-brand-gold">Expert Home Tuitions</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            100% result-oriented home and online tuitions in Hyderabad. Personalized learning designed to guarantee success.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Link to="/register" className="w-full sm:w-auto px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-full shadow-xl hover:shadow-primary-500/25 transition-all text-lg flex items-center justify-center gap-2">
              Find a Tutor <ChevronRight className="w-5 h-5" />
            </Link>
            <Link to="/contact" className="w-full sm:w-auto px-8 py-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-bold rounded-full shadow-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all text-lg flex items-center justify-center gap-2">
              Contact Us <Phone className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Stats Banner */}
      <section className="w-full max-w-6xl mx-auto px-4 mb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-700">
          {[
            { label: "Active Tutors", value: "500+", icon: Users },
            { label: "Success Rate", value: "100%", icon: Trophy },
            { label: "Coverage Areas", value: "50+", icon: MapPin },
            { label: "Subjects Taught", value: "100+", icon: BookOpen }
          ].map((stat, i) => (
            <div key={i} className="text-center space-y-2">
              <div className="flex justify-center mb-3">
                <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                  <stat.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
              <h3 className="text-3xl font-black text-slate-900 dark:text-white">{stat.value}</h3>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AEO FAQ Section */}
      <FAQ />

      <ContactWidget />
    </div>
  );
};

export default Home;
